import { db } from "../firebase";
import { collection, deleteDoc, doc, setDoc, serverTimestamp, getDocs, getDoc, onSnapshot } from "firebase/firestore";

/**
 * Deletes all user-owned Firestore data we currently store.
 * NOTE: Firestore has no "delete collection" API; we must delete documents manually.
 * This mirrors your existing logic.
 */
export async function deleteUserFirestoreData({ uid }) {
  if (!uid) throw new Error("deleteUserFirestoreData: uid is required");

  // keep this list in ONE place (here), not scattered across UI
  const subcollections = [
    "flashcards_de",
    "flashcards_en",
    "favoriteStories_de",
    "favoriteStories_en",
    "storyProgress_de",
    "storyProgress_en",

    // streak is a collection in your current structure: users/{uid}/streak/{docId}
    "streak",
  ];

  for (const sub of subcollections) {
    const ref = collection(db, "users", uid, sub);
    const snap = await getDocs(ref);

    for (const d of snap.docs) {
      await deleteDoc(doc(db, "users", uid, sub, d.id));
    }
  }

  // delete main user doc last
  await deleteDoc(doc(db, "users", uid));
}

/**
 * Create/update the user's profile doc after signup.
 * Path: users/{uid}
 */
export async function upsertUserProfileOnSignup({
    uid,
    name,
    email,
    consent,      // { acceptTermsPrivacy, marketingOptIn }
    versions,     // { terms, privacy, cookie, marketing }
  }) {
    if (!uid) throw new Error("upsertUserProfileOnSignup: uid is required");
    if (!email) throw new Error("upsertUserProfileOnSignup: email is required");
  
    const ref = doc(db, "users", uid);
  
    await setDoc(
      ref,
      {
        name: name ?? "",
        email,
        createdAt: serverTimestamp(),
        consents: {
          termsAndPrivacy: {
            accepted: !!consent?.acceptTermsPrivacy,
            acceptedAt: serverTimestamp(),
            versions: {
              terms: versions?.terms ?? null,
              privacy: versions?.privacy ?? null,
              cookie: versions?.cookie ?? null,
            },
            method: "checkbox",
          },
          marketingEmails: {
            optedIn: !!consent?.marketingOptIn,
            optedInAt: consent?.marketingOptIn ? serverTimestamp() : null,
            version: versions?.marketing ?? null,
          },
        },
      },
      { merge: true }
    );
  }

export async function upsertUserProfileAfterOAuth({
  uid,
  name,
  email,
  consent,   // { acceptTermsPrivacy, marketingOptIn }
  versions,  // { terms, privacy, cookie, marketing }
}) {
  if (!uid) throw new Error("upsertUserProfileAfterOAuth: uid is required");

  const ref = doc(db, "users", uid);

  await setDoc(
    ref,
    {
      // profile
      name: name ?? "",
      ...(email ? { email } : {}),

      // consents
      consents: {
        termsAndPrivacy: {
          accepted: !!consent?.acceptTermsPrivacy,
          acceptedAt: serverTimestamp(),
          versions: {
            terms: versions?.terms ?? null,
            privacy: versions?.privacy ?? null,
            cookie: versions?.cookie ?? null,
          },
          method: "checkbox",
        },
        marketingEmails: {
          optedIn: !!consent?.marketingOptIn,
          optedInAt: consent?.marketingOptIn ? serverTimestamp() : null,
          version: versions?.marketing ?? null,
        },
      },
    },
    { merge: true }
  );
}

  /**
 * Subscribe to the user profile doc (users/{uid}).
 * Calls onData with doc data (or null if doc doesn't exist).
 * Returns unsubscribe().
 */
export function subscribeUserProfile({ uid, onData, onError }) {
    if (!uid) throw new Error("subscribeUserProfile: uid is required");
    if (typeof onData !== "function") throw new Error("subscribeUserProfile: onData must be a function");
  
    const ref = doc(db, "users", uid);
  
    return onSnapshot(
      ref,
      (snap) => {
        onData(snap.exists() ? snap.data() : null);
      },
      (err) => {
        if (onError) onError(err);
      }
    );
  }

export async function getUserConsentStatus(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return { exists: false, acceptedTerms: false };

    const data = snap.data();
    const acceptedTerms = !!data?.consents?.termsAndPrivacy?.accepted;

    console.log("[getUserConsentStatus]", { uid, acceptedTerms });
    return { exists: true, acceptedTerms };
  } catch (e) {
    console.error("[getUserConsentStatus] ERROR", { uid, code: e?.code, message: e?.message });
    throw e;
  }
}

export async function setAnalyticsConsent({ uid, enabled }) {
  if (!uid) throw new Error("setAnalyticsConsent: uid is required");

  const ref = doc(db, "users", uid);

  await setDoc(
    ref,
    {
      consents: {
        analytics: {
          enabled: !!enabled,
          decidedAt: serverTimestamp(),
          method: "in_app_modal",
        },
      },
    },
    { merge: true }
  );
}
  
  
  